import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMap, {
  Source,
  Layer,
  MapRef,
  ViewStateChangeEvent,
} from 'react-map-gl';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import mapboxgl, { GeoJSONSourceOptions, LngLatLike } from 'mapbox-gl';
import { multiPolygon } from '@turf/helpers';
import circle from '@turf/circle';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import utils from 'utils';
import { MapValue } from 'models/Listing';
import useCallbackRef from 'hooks/useCallbackRef';
import { states, statePoints } from './states';
import { MAP_MAPBOX_STYLES } from 'data/global';

import mapStyles from './mapStyles';

import './Map.styles.scss';

export type Coords = [number, number]; // longitude, latitude

type MapProps = {
  className?: string;
  value?: MapValue;
  onChange?: (value: MapValue | null) => void;
};

export const mapBounds: mapboxgl.LngLatBoundsLike = [
  [93.89232721246697, -50.28361257144317],
  [-174.2282816715525, -9.644123715797136],
];

export const CIRCLE_RADIUS_IN_KM = 150;
export const BORDER_ZOOM_LEVEL = 4.5;

const Map: React.FC<MapProps> = (props) => {
  const { className, value, onChange } = props;
  const [map, mapRef] = useCallbackRef<MapRef>();
  const [zoom, setZoom] = useState<number>(-1);
  const { t } = useTranslation();

  const featuresBBoxCache = useRef(
    states.features.map((feature) => ({
      turfPolygon: multiPolygon(feature.geometry.coordinates as any),
      title: feature.properties.STATE_NAME,
    })),
  );

  const mapFliedTo = useRef(false);
  const [hoveredStateCode, setHoveredStateCode] = useState<Coords | null>(null);

  const [center, setCenter] = useState<Coords | null>(null);

  const onZoom = useCallback((e: ViewStateChangeEvent) => {
    setZoom(e.viewState.zoom);
  }, []);

  const onHover = useCallback(
    (event: mapboxgl.MapLayerMouseEvent) => {
      const {
        properties: { STATE_CODE } = {
          STATE_CODE: null,
        },
      } = (event.features && event.features[0]) || {};

      if (!STATE_CODE) {
        if (hoveredStateCode) setHoveredStateCode(null);
        return;
      }

      setHoveredStateCode(STATE_CODE);
    },
    [hoveredStateCode],
  );

  const onClick = useCallback(
    (event: mapboxgl.MapLayerMouseEvent) => {
      const {
        properties: { STATE_CODE } = {
          STATE_CODE: null,
        },
      } = (event.features && event.features[0]) || {};

      if (!STATE_CODE) return;

      const stateCenter = statePoints.features.find(
        (f) => f.properties.STATE_CODE === STATE_CODE,
      );

      if (!stateCenter) return;

      map.flyTo({
        center: stateCenter.geometry.coordinates as LngLatLike,
        zoom: BORDER_ZOOM_LEVEL + 1.5,
        essential: true,
      });
    },
    [map],
  );

  const filter = useMemo(
    () => ['==', ['get', 'STATE_CODE'], `${hoveredStateCode}`],
    [hoveredStateCode],
  );

  const blueCircle = useMemo(
    () =>
      center
        ? circle(center, CIRCLE_RADIUS_IN_KM, { units: 'kilometers' })
        : null,
    [center],
  );

  const blueCircleCenterGeojson = useMemo(() => {
    if (!center) return null;
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: center,
          },
        },
      ],
    } as GeoJSONSourceOptions['data'];
  }, [center]);

  const selectedState = useMemo(() => {
    if (!center) return null;

    return Object.values(featuresBBoxCache.current).find(({ turfPolygon }) =>
      booleanPointInPolygon(center, turfPolygon),
    )?.title;
  }, [center]);

  const debouncedOnChange = useRef(utils.debounce(onChange, 150)).current;

  useEffect(() => {
    if (!selectedState || !center) return;

    if (zoom > BORDER_ZOOM_LEVEL) {
      debouncedOnChange?.({
        state: statePoints.features.find(
          (f) => f.properties.STATE_NAME === selectedState,
        ).properties.STATE_NAME_ABBR,
        latitude: center[1],
        longitude: center[0],
      });
    } else {
      debouncedOnChange?.(null);
    }
  }, [center, selectedState, debouncedOnChange, zoom]);

  const valueDep = !value ? value : `${value.longitude},${value.latitude}`;

  useEffect(() => {
    const coords = !value
      ? null
      : ([value.longitude, value.latitude] as Coords);
    if (coords?.[0] === center?.[0] && coords?.[1] && center?.[1]) return;

    if (!mapFliedTo.current && map) {
      if (valueDep) {
        map.flyTo({ center: coords, zoom: BORDER_ZOOM_LEVEL + 1.5 });
      }
      mapFliedTo.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueDep, map]);

  return (
    <div className={classNames('map', className)}>
      <div>
        <ReactMap
          style={{
            cursor: hoveredStateCode ? 'pointer' : 'grab',
          }}
          initialViewState={{
            bounds: mapBounds,
          }}
          ref={mapRef}
          doubleClickZoom={false}
          onMove={({ viewState: { longitude, latitude } }) =>
            setCenter([longitude, latitude])
          }
          onZoom={onZoom}
          onMouseMove={onHover}
          onDrag={(e) =>
            setCenter([e.viewState.longitude, e.viewState.latitude])
          }
          onClick={onClick}
          maxBounds={mapBounds}
          mapStyle={MAP_MAPBOX_STYLES}
          interactiveLayerIds={['states']}
        >
          <Source
            type="geojson"
            id="states"
            data={states as GeoJSONSourceOptions['data']}
          >
            <Layer
              beforeId="waterway-label"
              id="states-h-outline"
              type="line"
              filter={filter}
              paint={mapStyles.stateHOutlines}
              maxzoom={BORDER_ZOOM_LEVEL}
            />
            <Layer
              beforeId="states-h-outline"
              {...mapStyles.stateHighlight}
              filter={filter}
              maxzoom={BORDER_ZOOM_LEVEL}
            />
            <Layer
              beforeId="states-highlight"
              id="states-outline"
              type="line"
              paint={mapStyles.stateOutlines}
              maxzoom={BORDER_ZOOM_LEVEL}
            />
            <Layer
              beforeId="states-outline"
              {...mapStyles.stateLayer}
              maxzoom={BORDER_ZOOM_LEVEL}
            />
          </Source>
          <Source
            type="geojson"
            id="stateslabels"
            data={statePoints as GeoJSONSourceOptions['data']}
          >
            <Layer
              {...mapStyles.stateName}
              filter={filter}
              maxzoom={BORDER_ZOOM_LEVEL}
            />
          </Source>
          {blueCircle && (
            <>
              <Source id="circle-fill" type="geojson" data={blueCircle}>
                <Layer
                  type="fill"
                  source="circle-fill"
                  minzoom={BORDER_ZOOM_LEVEL}
                  paint={mapStyles.blueCircle}
                />
                <Layer
                  type="line"
                  source="circle-fill"
                  minzoom={BORDER_ZOOM_LEVEL}
                  paint={mapStyles.blueOutline}
                />
              </Source>
              <Source
                id="helper-marker"
                type="geojson"
                data={blueCircleCenterGeojson}
              >
                <Layer
                  type="symbol"
                  minzoom={BORDER_ZOOM_LEVEL}
                  source="helper-marker"
                  layout={mapStyles.icon}
                />
              </Source>
            </>
          )}
        </ReactMap>
      </div>

      {zoom >= BORDER_ZOOM_LEVEL && (
        <>
          <button
            type="button"
            className="map__back"
            onClick={() => map.flyTo({ zoom: 0, duration: 1500 })}
          >
            {t('cancel')}
          </button>
          {!!selectedState && <p className="map__title">{selectedState}</p>}
        </>
      )}

      <p className="map__description">
        {t(
          zoom > BORDER_ZOOM_LEVEL
            ? 'locationDescription2'
            : 'locationDescription',
          { radius: CIRCLE_RADIUS_IN_KM },
        )}
      </p>
    </div>
  );
};

export default Map;
